/**
 * ЕДИНСТВЕННЫЙ перехватчик 409 для `/admin/game-data/**`.
 *
 * Экраны про оптимистичную блокировку не знают ничего: сервис вешает на запрос снимок
 * строки (`optimistic`), а всё остальное происходит здесь.
 *
 * Что делает на 409:
 *   1. берёт актуальную строку из `current` ответа (доп. GET НЕ делает — за это время
 *      строку успели бы поправить ещё раз);
 *   2. сливает три состояния (`optimistic-lock.ts`);
 *   3. конфликтов нет — молча пересохраняет с новой версией и просит показать тост;
 *      конфликты есть — ждёт человека и пересохраняет то, что он выбрал;
 *   4. человек отказался — реджектит `ConflictCancelledError`, экран просто ничего не делает.
 *
 * Ответ повторного запроса возвращается вызывающему коду как обычный успешный ответ:
 * `await api.updateOrnament(...)` либо резолвится слитой строкой, либо реджектится.
 */

import { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { conflictBridge } from './conflict-bridge';
import { threeWayMerge } from './optimistic-lock';
import {
  ConflictCancelledError,
  GameDataConflictDto,
  GameDataPayload,
  MergeSpec,
} from '../../types/game-data.types';

const GAME_DATA_PREFIX = '/admin/game-data/';

/**
 * Сколько раз подряд пытаемся слить. Второй 409 подряд — это уже двое, жмущих «Сохранить»
 * одновременно; каждый круг берёт свежую версию, поэтому прогресс есть, но крутить бесконечно нельзя.
 */
const MAX_MERGE_ROUNDS = 3;

/** Тело, которое реально ушло на сервер: axios к этому моменту уже сериализовал его в строку. */
const parseSentBody = (data: unknown): GameDataPayload | null => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as GameDataPayload;
    } catch {
      return null;
    }
  }
  if (data && typeof data === 'object') return data as GameDataPayload;
  return null;
};

export const attachGameDataConflictInterceptor = (client: AxiosInstance): void => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError): Promise<AxiosResponse> => {
      const config = error.config as InternalAxiosRequestConfig | undefined;
      const response = error.response;

      if (!config || !response || response.status !== 409) throw error;
      if (!config.url?.includes(GAME_DATA_PREFIX)) throw error;

      const round = config._gameDataMergeRound ?? 0;
      if (round >= MAX_MERGE_ROUNDS) throw error;

      const meta = config.optimistic;
      const body = response.data as GameDataConflictDto | undefined;

      // Без снимка или без `current` слить нечего. Сюда же попадает редкий 409 на весь
      // `/bulk` — там `current` не приходит, и разбирается он на экране.
      if (!meta || !body?.current) throw error;

      const mine = parseSentBody(config.data);
      if (!mine) throw error;

      const spec = meta.spec as MergeSpec<unknown>;
      const theirs = spec.project(body.current);
      const { merged, conflicts } = threeWayMerge(meta.base, mine, theirs, spec);

      let resolved: GameDataPayload = merged;

      if (conflicts.length > 0) {
        const answer = await conflictBridge.resolve({
          merged,
          conflicts,
          theirs: body.current,
          spec,
        });
        if (!answer) throw new ConflictCancelledError();
        resolved = answer;
      }

      const retry = await client({
        ...config,
        data: { ...resolved, version: spec.version(body.current) },
        // Снимок обновляем: если на повторе снова прилетит 409, сливать надо уже от чужой строки.
        optimistic: { ...meta, base: theirs },
        _gameDataMergeRound: round + 1,
      });

      if (conflicts.length === 0) conflictBridge.notifyAutoMerged(spec);
      return retry;
    },
  );
};

export default attachGameDataConflictInterceptor;
